import { useEffect, useRef, useState } from 'react';

/** 값이 delay 동안 변하지 않을 때만 갱신한다. 검색어 입력마다 요청하지 않기 위함. */
export function useDebounced<T>(value: T, delay = 350): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debounced;
}

export type UrlPatch = Record<string, string | number | undefined>;
export type UpdateUrl = (patch: UrlPatch, resetPage?: boolean) => void;

/**
 * 목록 화면 12곳이 저마다 만들어 쓰던 URL 갱신 함수를 하나로 모은다.
 *
 * 손으로 각자 적다 보니 세 가지로 갈라져 있었다.
 *   - 페이지 리셋: `next.set('page','1')` 8곳 / `next.delete('page')` 1곳(ReportPage)
 *   - 빈 값 판정: `v === undefined || v === ''` 대다수 / `if (v)` 3곳
 *     (`if (v)` 는 문자열 '0' 도 지워버린다 - 지금은 '0' 을 쓰는 필터가 없어서
 *     안 터졌을 뿐이다)
 * 여기서는 더 안전한 쪽(빈 값 판정)과 더 깔끔한 쪽(delete)으로 통일한다.
 */
export function useUrlUpdate(
  params: URLSearchParams,
  setParams: (next: URLSearchParams, opts?: { replace?: boolean }) => void,
): UpdateUrl {
  return (patch, resetPage = true) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === undefined || v === '') next.delete(k);
      else next.set(k, String(v));
    }
    if (resetPage) next.delete('page');
    setParams(next, { replace: true });
  };
}

/**
 * URL 의 한 검색어 칸을 다룬다. URL 을 유일한 진실로 삼되, 타이핑하는 동안은
 * 매 글자마다 URL 을 갱신하지 않고 로컬에서 보여주다가, 멈추면 그때 URL 에 반영한다.
 *
 * 이전에는 `useState(params.get('q') ?? '')` 로 처음 한 번만 URL 을 읽고, 그 뒤로는
 * URL 이 바뀌어도(뒤로가기, 다른 곳에서 같은 화면으로 다른 검색어를 걸고 들어오는
 * 링크 등) 안 따라갔다 - 입력칸과 실제 조회 결과가 서로 다른 값을 보여줄 수 있었다.
 *
 * 여기서는 "로컬에 아직 흘려보내지 않은 입력(draft)이 있으면 그걸, 없으면 URL 값"을
 * 보여준다. debounce 가 끝나면 draft 를 URL 에 써넣고 draft 를 비운다 - 그 뒤로는
 * 다시 URL 을 그대로 따라간다. 그래서 draft 가 없는 동안은 URL 이 바뀌는 즉시 반영된다.
 */
export function useUrlSearch(
  params: URLSearchParams,
  update: UpdateUrl,
  key = 'q',
  delay = 350,
): [string, (v: string) => void] {
  const committed = params.get(key) ?? '';
  const [draft, setDraft] = useState<string | null>(null);
  const value = draft ?? committed;
  const debounced = useDebounced(value, delay);

  useEffect(() => {
    if (draft === null || debounced !== draft) return;
    if (debounced !== committed) update({ [key]: debounced || undefined });
    setDraft(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  return [value, setDraft];
}

/**
 * useUrlSearch 의 여러-칸 버전. ReportPage 처럼 정의마다 검색 필터 개수가
 * 달라지는(=매번 다른 키 집합에 훅을 몇 번 부를지 알 수 없는) 화면에서 쓴다.
 * 원리는 같다 - 칸마다 draft 가 있으면 그걸, 없으면 URL 값을 보여준다.
 */
export function useUrlSearchFields(
  params: URLSearchParams,
  update: UpdateUrl,
  keys: string[],
  delay = 350,
  defaults: Record<string, string> = {},
): [Record<string, string>, (key: string, v: string) => void] {
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const draftsKey = JSON.stringify(drafts);
  const debouncedKey = useDebounced(draftsKey, delay);

  useEffect(() => {
    const pending = JSON.parse(debouncedKey || '{}') as Record<string, string>;
    if (Object.keys(pending).length === 0) return;

    const patch: UrlPatch = {};
    let changed = false;
    for (const [k, v] of Object.entries(pending)) {
      if ((params.get(k) ?? '') !== v) {
        patch[k] = v || undefined;
        changed = true;
      }
    }
    if (changed) update(patch, false);
    setDrafts({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedKey]);

  const values: Record<string, string> = {};
  for (const k of keys) values[k] = k in drafts ? drafts[k]! : (params.get(k) ?? defaults[k] ?? '');

  const setValue = (key: string, v: string) => setDrafts((d) => ({ ...d, [key]: v }));

  return [values, setValue];
}

/**
 * 메모 같은 textarea 를 기본은 짧게(rows) 두고, 글이 길어지면 스크롤 대신
 * 내용 높이만큼 늘어나게 한다. CSS 만으로는(글자 수에 따라) 못 하므로
 * scrollHeight 를 재서 높이를 직접 맞춘다 - 입력값이 바뀔 때마다 다시 잰다.
 */
export function useAutoGrowTextarea(value: string) {
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);

  return ref;
}
